import { FormBlock } from '@qbe/ads-blocks-forms';
import { Dropdown } from '@qbe/ads-core';
import CurrencyField from 'components-ds/CurrencyField';
import { COVER_CATEGORIES } from 'constants/domain-specific';
import { useAppContent, useCoverDetails } from 'hooks';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Cover, CoverItem, CoverTypes } from 'types/cover';
import { v4 } from 'uuid';

import Modal from '~/components-ds/Modal';
import { useTrackOptimizeActivate } from '~/hooks/useTrackOptimizeActivate';
import { updateBlanketCovers } from '~/slices/coverDetails';

interface UpdateBlanketCoverItemProps {
  serialNumber: CoverTypes;
  selectedCover?: CoverItem;
  onUpdate: typeof updateBlanketCovers;
  onClose: () => void;
}

const UpdateBlanketCoverItemModal: React.FC<UpdateBlanketCoverItemProps> = ({
  serialNumber,
  selectedCover,
  onUpdate,
  onClose,
}) => {
  const { getTextContent: getSharedContent } = useAppContent({ basePath: 'shared' });
  const { getTextContent } = useAppContent({
    basePath: 'dsme.coverDetailsPortableItemsAddCategoryModal',
    tokens: { minValue: () => String(MIN_VALUE) },
  });

  const dispatch = useDispatch();
  const { blanketCovers } = useCoverDetails();
  const [coverCategory, setCoverCategory] = useState<string>(selectedCover?.category || '');
  const [coverValue, setCoverValue] = useState<string | null>(
    selectedCover?.value.toString() || ''
  );
  const [coverError, setCoverError] = useState(false);
  const MIN_VALUE = 1000;

  // Tracks the "optimize.activate" event when the component mounts or re-renders.
  // This is used to notify Webtrends (or other analytics tools) for visibility tracking
  // and ensures dynamic content is properly handled.
  useTrackOptimizeActivate();
  /**
   * @TODO
   * move add and edit into redux
   */

  const handleUpdate = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const id = selectedCover?.id || v4();
    const code = COVER_CATEGORIES.find((o) => o.description === coverCategory)?.id || '';
    const categoryItem = {
      id,
      code,
      category: coverCategory,
      value: parseInt(coverValue ?? '', 10),
      serialNumber,
    };
    let updatedCategoryList: CoverItem[];
    if (selectedCover && blanketCovers) {
      updatedCategoryList = blanketCovers.map((o) =>
        o.id === selectedCover.id ? categoryItem : o
      );
    } else {
      updatedCategoryList = blanketCovers ? [...blanketCovers, categoryItem] : [categoryItem];
    }
    updatedCategoryList && dispatch(onUpdate(updatedCategoryList));

    onClose();
  };

  const options = (() => {
    const categories = COVER_CATEGORIES.filter(
      (o) => !blanketCovers?.map((option) => option.category)?.includes(o.description)
    );
    if (selectedCover)
      categories.push({
        id: selectedCover.id,
        description: selectedCover.category,
      });
    return categories;
  })();

  const [touchedCoverCategory, setTouchedCoverCategory] = useState(false);

  return (
    <Modal
      responsiveHeight
      open
      id="update-blanket-cover-modal"
      title={getTextContent('title')}
      primaryBtnProps={{
        label: getSharedContent('updateButtonLabel'),
        'data-track': 'Update - cover item',
        onClick: handleUpdate,
        disabled: !coverCategory || !coverValue || coverError,
      }}
      secondaryBtnProps={{
        'data-track': 'Cancel - cover item',
        onClick: onClose,
        label: getSharedContent('cancelModalButtonLabel'),
      }}
    >
      <>
        <FormBlock preset="dropdown" maxWidth="100%">
          <Dropdown
            optionList={options.map((o) => ({
              label: o.description,
              value: o.description,
              'data-track': 'Add category',
              'data-track-value': 'capture',
            }))}
            onChange={(e) => {
              setCoverCategory(e.target.value as Cover);
            }}
            onBlur={(e) => {
              setCoverCategory(e.target.value as Cover);
              setTouchedCoverCategory(true);
            }}
            defaultValue={selectedCover?.category}
            data-track-label="cover category"
            error={!coverCategory && touchedCoverCategory}
            helperText={
              !coverCategory && touchedCoverCategory
                ? getTextContent('fields.categoryDropdown.invalidText')
                : getTextContent('fields.categoryDropdown.helperText')
            }
            label={getTextContent('fields.categoryDropdown.label')}
            id="coverCategory"
            required
          />
        </FormBlock>
        <FormBlock preset="input" maxWidth="100%">
          <CurrencyField
            id="categoryValue"
            name="category value"
            label={getTextContent('fields.valueInput.label')}
            data-track="category value"
            data-track-value="mask"
            helperText={getTextContent('fields.valueInput.helperText')}
            errorText={getTextContent('fields.valueInput.invalid')}
            defaultValue={coverValue ?? undefined}
            fieldValidation={{
              validations: {
                numericOnly: true,
                required: true,
                minValue: MIN_VALUE,
              },
              onValid: setCoverValue,
              onError: setCoverError,
            }}
            required
          />
        </FormBlock>
      </>
    </Modal>
  );
};

export default UpdateBlanketCoverItemModal;
